/* ============================================================================
   DATA — the Uniform Builder's layers, products, colours and avatar shots.

   Layers and their order are Figma's (D Uniform Builder 1752:159100 · M
   1535:15463): Layer 01 Scrub Tops → 06 Accessories. Tops and pants are the
   uniform's required layers (the Outfit Layer component's "Required" tag;
   only the optional slots carry a remove ×).

   Products are the Figma cards, one per Ghost Image variant (1519:9092), with
   live wearfigs.com prices, sizes and lengths (pulled 2026-10-07 from each
   PDP's Navy variant). Card names are Figma's, except the two Isabels, which
   Figma calls Straight Leg but are wide-leg pants on the site. Montex, the
   Cropped Sydney and the 3447 Zip aren't sold in Navy; they keep Figma's
   ghosts and the closest live variant's data. Photos are the PDPs' first two
   shots, vendored into assets/products/<id>/ (none for the Cropped Sydney —
   its only colours are Bonsai and Purple Haze).

   Ghosts are the Figma Ghost Image set's own renders (the Dropbox WOMENS
   navy ghosts; the cap and New Balance pairs are PDP ghost shots with the
   backdrop removed), saved square at 800px.
   ========================================================================== */

export const LAYERS = [
  { id: 'tops', step: 1, title: 'Scrub Tops', name: 'Scrub Top', chip: 'Top', required: true },
  { id: 'pants', step: 2, title: 'Scrub Pants', name: 'Scrub Pants', chip: 'Pants', required: true },
  { id: 'underscrubs', step: 3, title: 'Underscrubs', name: 'Underscrub', chip: 'Under' },
  { id: 'outerwear', step: 4, title: 'Outerwear', name: 'Outerwear', chip: 'Outer' },
  { id: 'footwear', step: 5, title: 'Shoes', name: 'Footwear', chip: 'Shoes' },
  { id: 'accessories', step: 6, title: 'Accessories', name: 'Accessory', chip: 'Acc.' },
];

const APPAREL_11 = ['XXS', 'XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL', '6XL'];
const APPAREL_7 = ['XXS', 'XS', 'S', 'M', 'L', 'XL', '2XL'];
const NB = ['M4/W5.5', 'M4.5/W6', 'M5/W6.5', 'M5.5/W7', 'M6/W7.5', 'M6.5/W8', 'M7/W8.5', 'M7.5/W9', 'M8/W9.5',
  'M8.5/W10', 'M9/W10.5', 'M9.5/W11', 'M10/W11.5', 'M10.5/W12', 'M11/W12.5', 'M11.5/W13', 'M12/W13.5', 'M13/W14.5'];
const TOP_TYPES = ['Regular', 'Petite'];
const LENGTHS = ['Regular', 'Petite', 'Tall'];
const PDP = 'https://www.wearfigs.com/products/';

/* `tint: false` keeps a product's own colourway when the uniform colour
   changes (the New Balance pairs); `embroidery` offers the add-on card. */
const product = (layer, id, name, price, sizes, handle, extra = {}) => ({
  layer, id, name, price, sizes, types: [], typeLabel: null, tint: true, embroidery: false,
  url: `${PDP}${handle}?color=${encodeURIComponent(extra.urlColor || 'Navy')}`,
  ghost: `assets/ghosts/${id}.webp`,
  photos: extra.noPhotos ? [] : [`assets/products/${id}/01.webp`, `assets/products/${id}/02.webp`],
  /* `photos` are navy on-model shots, which Quick Buy adds in navy — not the
     Montex's (Dark Harbor) or the cap's (product shots, no model) */
  modelPhotos: !extra.noPhotos,
  ...extra,
});
const top = (id, name, price, handle, extra) => product('tops', id, name, price, APPAREL_11, handle, { embroidery: true, ...extra });
const pant = (id, name, price, sizes, handle, extra) => product('pants', id, name, price, sizes, handle, { types: LENGTHS, typeLabel: 'Length', ...extra });

export const PRODUCTS = [
  top('catarina', 'Catarina One-Pocket', 42, 'womens-catarina-scrub-top', { types: TOP_TYPES, typeLabel: 'Type', lab: '/experiments/10-pdp-prototype/' }),
  top('casma', 'Casma Three-Pocket', 42, 'womens-casma-scrub-top'),
  top('rafaela', 'Rafaela Oversized', 42, 'womens-rafaela-scrub-top'),
  top('montex', 'Montex Mandarin Collar', 48, 'womens-montex-scrub-top', { urlColor: 'Dark Harbor', modelPhotos: false }),

  pant('zamora', 'Zamora Jogger', 52, APPAREL_11, 'womens-zamora-scrub-pants'),
  pant('zamora-hr', 'Zamora High-Rise Jogger', 52, APPAREL_11, 'womens-zamora-high-waisted-yoga-waistband-scrub-pants'),
  pant('zamora-ruched', 'Zamora Ruched Jogger', 52, APPAREL_7, 'womens-mid-rise-zamora-ruched-jogger-scrub-pants'),
  pant('kade', 'Kade Cargo', 52, APPAREL_11, 'womens-kade-cargo-scrub-pants'),
  pant('kade-hr', 'Kade High-Rise Cargo', 52, APPAREL_7, 'womens-figs-high-waisted-kade-scrub-pants'),
  pant('isabel', 'Isabel Wide-Leg', 58, APPAREL_7, 'womens-isabel-scrub-pants', { lab: '/experiments/10-pdp-prototype/pants.html' }),
  pant('isabel-ruched', 'Isabel Ruched Wide-Leg', 58, APPAREL_7, 'womens-mid-rise-isabel-ruched-wide-leg-scrub-pants'),
  pant('livingston', 'Livingston Basic', 48, APPAREL_11, 'womens-livingston-high-waisted-scrub-pants'),
  pant('livingston-ruched', 'Livingston Ruched', 48, APPAREL_7, 'womens-mid-rise-livingston-ruched-straight-leg-scrub-pants'),

  product('underscrubs', 'salta-ss', 'Salta SS Underscrub', 48, APPAREL_7, 'womens-salta-shortsleeve-underscrub'),
  product('underscrubs', 'salta-ls', 'Salta LS Underscrub', 58, APPAREL_11, 'womens-salta-under-scrubs'),

  product('outerwear', 'sydney', 'Sydney Performance Jacket', 98, APPAREL_11, 'womens-sydney-scrub-jacket', { embroidery: true }),
  product('outerwear', 'sydney-crop', 'Sydney Cropped Jacket', 98, APPAREL_7, 'womens-cropped-sydney-outerwear',
    { embroidery: true, noPhotos: true, urlColor: 'Bonsai' }),

  product('footwear', 'nb-3447', 'New Balance 3447', 158, NB, 'unisex-figs-new-balance-3447-shoes', { tint: false, colorway: 'Navy' }),
  product('footwear', 'nb-327', 'New Balance 327', 128, NB, 'unisex-figs-new-balance-327-shoes', { tint: false, colorway: 'Grey/Navy', urlColor: 'Grey/Navy' }),
  product('footwear', 'nb-3447-zip', 'New Balance 3447 Zip', 158, NB, 'unisex-figs-new-balance-3447-zip-shoes', { tint: false, colorway: 'Graphite', urlColor: 'Graphite' }),

  product('accessories', 'scrub-cap', 'Scrub Cap', 22, ['O/S'], 'unisex-modern-classic-scrub-cap', { modelPhotos: false }),
];
export const productById = (id) => PRODUCTS.find((p) => p.id === id);
/* where a product's links go: the lab's PDP prototype in the uniform's colour
   where there is one (Catarina, Isabel — the PDP falls back to its own colour
   for one it doesn't carry), else wearfigs.com */
export const productUrl = (p, slug) => (p.lab ? `${p.lab}?color=${slug || 'navy'}` : p.url);
export const isExternal = (u) => /^https?:/.test(u);

/* Filter Drawer → Select a Color (D 1752:158640 · M 1752:158579): Figma's 20
   tiles in its order, on its fills (the PLP's --product-color tokens). Navy is
   the colour the ghosts and the avatar were shot in; every other colour is
   recoloured from them in the browser (recolor.js). `dark` sets white copy. */
export const COLORS = [
  ['black', 'Black', '#2b2c30', 1], ['charcoal', 'Charcoal', '#46444c', 1], ['smokey-taupe', 'Smokey Taupe', '#7b6660', 1], ['graphite', 'Graphite', '#8d8d96', 1],
  ['harbor-grey', 'Harbor Grey', '#869aab', 1], ['off-white', 'Off White', '#efeadf', 0], ['optic-white', 'Optic White', '#edf1ff', 0], ['navy', 'Navy', '#2b2e43', 1],
  ['deep-royal-blue', 'Deep Royal Blue', '#333e83', 1], ['royal-blue', 'Royal Blue', '#285ad3', 1], ['ceil-blue', 'Ceil Blue', '#5b7ebd', 1], ['blue-horizon', 'Blue Horizon', '#007ab2', 1],
  ['deep-reef', 'Deep Reef', '#103044', 1], ['dark-harbor', 'Dark Harbor', '#1c3a3e', 1], ['caribbean-blue', 'Caribbean Blue', '#274e55', 1], ['lagoon', 'Lagoon', '#038786', 1],
  ['seaglass', 'Seaglass', '#77a7ab', 1], ['forest-green', 'Forest Green', '#264a48', 1], ['moss', 'Moss', '#56584c', 1], ['martini-olive', 'Martini Olive', '#766539', 1],
].map(([slug, name, hex, dark]) => ({ slug, name, hex, dark: Boolean(dark), source: slug === 'navy' }));
export const colorBySlug = (slug) => COLORS.find((c) => c.slug === slug);
export const SOURCE_COLOR = 'navy';

/* ----------------------------------------------------------------------------
   AVATAR — the AI model (ChatGPT image generation, 2026-10-07), dressed one
   layer at a time in Figma's order with the navy ghosts as references, plus
   the full uniform minus each layer that shows, and pants alone. One
   representative style per layer: Catarina, Zamora High-Rise, Salta LS,
   Sydney (worn open), NB 327 and the scrub cap.

   Those 12 shots give eight bodies (the top / pants / underscrub / jacket
   combinations they show). The generated shots share one pose to within
   1.5px, so the cap and the sneakers move between them as patches: every
   body comes with and without each, 32 shots in all, named
   <body>[_cap][_shoes] — the native ChatGPT shot wherever one exists. A
   footwear or accessory pick therefore always shows. Each shot has a garment
   mask (<shot>-mask.webp) that recolor.js uses to change the uniform colour
   without touching skin, hair, socks or the sneakers.
   ---------------------------------------------------------------------------- */
const BODIES = [
  ['base', []],
  ['top', ['tops']],
  ['top-pants', ['tops', 'pants']],
  ['top-pants-under', ['tops', 'pants', 'underscrubs']],
  ['top-pants-under-outer', ['tops', 'pants', 'underscrubs', 'outerwear']],
  ['pants-under-outer', ['pants', 'underscrubs', 'outerwear']],
  ['top-under-outer', ['tops', 'underscrubs', 'outerwear']],
  ['pants', ['pants']],
];
export const AVATAR_SHOTS = BODIES.flatMap(([body, layers]) => [[0, 0], [0, 1], [1, 0], [1, 1]].map(([cap, shoes]) => {
  const id = `${body}${cap ? '_cap' : ''}${shoes ? '_shoes' : ''}`;
  return {
    id, body, src: `assets/avatar/${id}.webp`, mask: `assets/avatar/${id}-mask.webp`,
    layers: [...layers, ...(shoes ? ['footwear'] : []), ...(cap ? ['accessories'] : [])],
  };
}));

/* The body is the one that costs least for the picks: showing a garment the
   shopper hasn't picked costs three times what leaving a pick off does, and
   an underscrub under an open jacket barely shows. Footwear and the cap then
   match exactly. */
const WEIGHT = { tops: 3, pants: 3, underscrubs: 1, outerwear: 3 };
export function shotFor(layers) {
  const want = new Set(layers);
  let body = BODIES[0];
  let bestCost = Infinity;
  for (const candidate of BODIES) {
    const has = new Set(candidate[1]);
    const jacket = want.has('outerwear') && has.has('outerwear');
    const w = (id) => (id === 'underscrubs' && jacket ? 0.3 : WEIGHT[id]);
    let cost = 0;
    has.forEach((id) => { if (!want.has(id)) cost += 3 * w(id); });
    want.forEach((id) => { if (id in WEIGHT && !has.has(id)) cost += w(id); });
    if (cost < bestCost) { body = candidate; bestCost = cost; }
  }
  const id = `${body[0]}${want.has('accessories') ? '_cap' : ''}${want.has('footwear') ? '_shoes' : ''}`;
  return AVATAR_SHOTS.find((s) => s.id === id);
}

/* Embroidery add-on (Quick Buy · M04 1536:149865): the card's typed sample
   line. Placements and prices are the shared configurator's
   (src/figs-embroidery/). */
export const EMBROIDERY = {
  preview: 'Madeline Smith, RN',
};

export const money = (n) => `$${n.toFixed(2)}`;
export const moneyShort = (n) => `$${Number.isInteger(n) ? n : n.toFixed(2)}`;
